/* eslint-disable tailwindcss/no-arbitrary-value */
import {
  Headline,
  HeadlineIconProps,
  HeadlineProps,
} from '@ui/components/patterns';
import { Body2 } from '@ui/components/tokens';
import { cn } from '@ui/lib/utils';

export interface ToastProps {
  className?: string;
  headline: HeadlineProps['headline']['content'];
  subtext?: string;
  type?: 'default' | 'fail' | 'success' | 'loading';
}

const typeConfigMap = {
  default: {
    classNames: {
      headline: 'text-on-secondary-surface',
      bg: 'bg-screen-surface',
    },
  },
  fail: {
    classNames: {
      headline: 'text-on-supplementary',
      icon: 'text-negative-strong',
      bg: 'bg-negative-weak',
    },

    iconFileName: 'ic_error_circle_filled' as const,
  },
  success: {
    classNames: {
      headline: 'text-on-supplementary',
      icon: 'text-positive-strong',
      bg: 'bg-positive-weak',
    },
    iconFileName: 'ic_check_circle_filled' as const,
  },
  loading: {
    classNames: {
      headline: 'text-on-secondary-surface',
      icon: 'animate-spin-slow',
      bg: 'bg-primary-surface',
    },
    iconFileName: 'ic_loading' as const,
  },
};

/** A fully custom toast that still maintains the animations and interactions. */
export function Toast({
  headline,
  subtext,
  type = 'default',
  className,
}: ToastProps) {
  const { classNames, ...config } = typeConfigMap[type];

  let iconOptions: HeadlineIconProps['icon'] | undefined;

  if ('iconFileName' in config) {
    iconOptions = { fileName: config.iconFileName };
  }

  if ('icon' in classNames && iconOptions) {
    iconOptions = { ...iconOptions, className: classNames.icon };
  }

  return (
    <div
      className={cn(
        'inline-flex w-[343px] flex-col items-center justify-center gap-2 rounded-2xl p-4',
        classNames.bg,
        className,
      )}
    >
      <Headline
        size="sm"
        position="end"
        icon={iconOptions}
        headline={{ content: headline, className: classNames.headline }}
      />

      {subtext && (
        <Body2 className="self-stretch text-sm leading-[18px] text-on-screen-surface">
          {subtext}
        </Body2>
      )}
    </div>
  );
}
